import { Box } from '@material-ui/core';
import { CheckoutSessionStatus, DownPayment, DownPaymentCollection } from '@nemuru-eng/js-api-client';

import { TotalLine } from '@/components/modules/_shared/CartSummary/components/CartTotalLine';
import { CartSummaryData } from '@/components/modules/_shared/CartSummary/types/CartSummaryData';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface CartSummaryDownPaymentProps {
  cartSummaryData: CartSummaryData;
  downPayment: DownPayment;
  discount?: number;
  checkoutSessionStatus?: CheckoutSessionStatus;
  strikethrough?: boolean;
}

export const CartSummaryDownPayment = ({
  cartSummaryData,
  downPayment,
  discount,
  checkoutSessionStatus,
  strikethrough,
}: CartSummaryDownPaymentProps) => {
  const showDownPaymentStatusChip = downPayment.downPaymentCollection !== DownPaymentCollection.NONE;

  const currency = cartSummaryData.currency;

  return (
    <Box display={'flex'} flexDirection={'column'} gridGap={4}>
      <TotalLine
        label={'DOWN_PAYMENT'}
        {...(showDownPaymentStatusChip && {
          labelSuffix: (
            <div style={{ marginLeft: 8, display: 'inline-flex', position: 'relative', top: 2 }}>
              <DownPaymentStatusChip status={checkoutSessionStatus} />
            </div>
          ),
        })}
        value={downPayment.amount.amount}
        currency={currency}
        strikethrough={strikethrough}
      />
      {discount !== undefined && discount > 0 && (
        <TotalLine label={'TOTAL_DISCOUNT'} value={discount} currency={currency} strikethrough={strikethrough} />
      )}
      <TotalLine
        strong
        label={'TOTAL_TO_FINANCE'}
        value={cartSummaryData.total - downPayment.amount.amount - (discount ?? 0)}
        currency={currency}
        strikethrough={strikethrough}
      />
    </Box>
  );
};

const DownPaymentStatusChip = ({ status }: { status: CheckoutSessionStatus | undefined }) => {
  const { palette, customBorderRadius } = useCustomTheme();

  const getDownPaymentStyleByStatus = (status: CheckoutSessionStatus | undefined) => {
    switch (status) {
      case CheckoutSessionStatus.CONFIRMED:
        return { color: palette.success.main, icon: 'check', label: 'PAID' };
      default:
        return null;
    }
  };

  const chipDetails = getDownPaymentStyleByStatus(status);

  if (!chipDetails) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gridGap: 4,
        padding: '2px 8px',
        position: 'relative',
        color: chipDetails.color,
        borderRadius: customBorderRadius.chip,
        overflow: 'hidden',
      }}
    >
      <Icon size={16}>{chipDetails.icon}</Icon>
      <Font variant="caption">
        <Text strong tId={chipDetails.label} />
      </Font>
      <div style={{ backgroundColor: chipDetails.color, inset: 0, position: 'absolute', opacity: 0.1 }} />
    </div>
  );
};
